<template>
  <div class="home flex-grow flex flex-col">
    <section class="text-center relative flex-grow flex justify-center">
      <modal name="rejectReplacementModal" :adaptive="true" :width="350" :height="300">
        <h3 class="text-center my-6">
          {{ $t('Would you like to inform the chef why you are rejecting this replacement') }}?
        </h3>
        <form class="px-8" autocomplete="off">
          <div class="mb-4">
            <label class="block text-gray-700 text-sm font-bold mb-2" for="rejectReason">
              {{ $t('Reason') }}
            </label>
            <input
              class="shadow appearance-none border rounded w-full py-2 px-3 text-gray-700 leading-tight focus:outline-none focus:shadow-outline"
              id="rejectReason"
              type="text"
              v-model="customerRejectReason"
            />
          </div>
          <div class="flex justify-center">
            <button
              class="flex items-center rounded-full bg-red-700 text-center px-6 py-2 font-light text-white"
              @click="handleConfirmRejectReplacement"
              type="button"
            >
              {{ $t('Reject') }}
            </button>
          </div>
        </form>
      </modal>
      <div class="w-full">
        <h1 class="text-4xl text-gray-800 pt-6 mb-4 font-bold">{{ $t('Order') }}</h1>
        <div
          v-for="(item, index) in itemsFiltered"
          :key="index"
          class="bg-white mx-3 my-6 rounded-lg shadow-lg flex justify-between px-4 py-6 text-lg"
        >
          <div class="flex items-center mr-3 text-left">
            <p>{{ item[0] }}</p>
          </div>
          <div class="flex items-center">
            <p class="bg-app-orange w-10 text-white px-3 py-1 rounded-lg">{{ item[1].quantity }}</p>
            <p class="font-bold mx-1">x</p>
            <p class="bg-indigo-600 text-white px-3 py-1 rounded-lg">${{ item[1].price.toFixed(2) }}</p>
            <p class="font-bold mx-1">=</p>
            <p class="bg-app-green text-white px-3 py-1 rounded-lg">
              ${{ (item[1].quantity * item[1].price).toFixed(2) }}
            </p>
          </div>
        </div>
        <div class="bg-white mx-3 my-6 rounded-lg shadow-lg flex justify-between px-4 py-6 text-lg">
          <div class="flex items-center">
            <eva-icon
              name="shopping-cart-outline"
              animation="pulse"
              width="35"
              height="35"
              fill="#384E77"
              class="mr-4"
            ></eva-icon>
            <p class="font-bold text-xl">{{ $t('Total') }}</p>
          </div>
          <p class="font-bold text-xl">$ {{ cartTotal.toFixed(2) }}</p>
        </div>

        <div class="flex items-center justify-between w-4/5 mx-auto mt-10 mb-4">
          <p class="text-2xl">{{ $t('Status') }}:</p>
          <div
            class="rounded-full text-center px-8 py-2 font-light text-white capitalize"
            :class="{
              'bg-green-400': order.status === 'approved' || order.status === 'ready',
              'bg-app-orange': order.status === 'pending' || order.status === 'replacementSent',
              'bg-red-700': order.status === 'rejected' || order.status === 'customerRejected',
            }"
          >
            {{ order.status == 'customerRejected' ? $t('Rejected by customer') : '' }}
            {{ order.status == 'approved' ? $t('Approved') : '' }}
            {{ order.status == 'pending' ? $t('Pending') : '' }}
            {{ order.status == 'rejected' ? $t('Rejected') : '' }}
          </div>
        </div>
        <div class="mt-16" v-show="order.status === 'approved'">
          <p class="text-2xl">{{ $t('Estimated Time') }}:</p>
          <p class="text-2xl uppercase">{{ order.estimatedTime }}</p>
          <p
            class="text-2xl"
            v-if="(countdownMinutes > 0 && countdownSeconds > 0) || (countdownMinutes == 0 && countdownSeconds > 0)"
          >
            {{ countdownMinutes }} {{ $t('mins') }} {{ countdownSeconds }}
            {{ $t('seconds until your order is ready') }}!
          </p>
          <p class="text-2xl" v-if="countdownMinutes <= 0 && countdownSeconds <= 0">
            {{ $t('Your order is on the way') }}
          </p>
        </div>
        <div class="mt-16" v-show="order.status === 'rejected'">
          <p class="text-2xl">{{ $t('Reason') }}:</p>
          <p class="text-2xl">{{ order.rejectReason }}</p>
        </div>
        <div class="mt-16" v-show="order.status === 'ready'">
          <p class="text-2xl">{{ $t("Your order is ready and on it's way to you!") }}</p>
        </div>
        <div class="flex justify-center">
          <button
            class="mr-4 mt-6 mb-6 rounded-full bg-red-600 text-center px-4 py-2 font-light text-white flex items-center"
            @click="handleGoToMenu"
          >
            <eva-icon
              name="corner-up-left-outline"
              animation="pulse"
              width="20"
              height="20"
              fill="#FFFFFF"
              class="mr-2"
            ></eva-icon>
            {{ $t('Menu') }}
          </button>
          <button
            class="ml-4 mt-6 mb-6 rounded-full bg-blue-600 text-center px-4 py-2 font-light text-white"
            @click="handleGoMyOrders"
          >
            {{ $t('My Orders') }}
          </button>
        </div>
      </div>
      <div v-show="order.status === 'replacementSent'" class="replacement-pane absolute bottom-0 bg-white">
        <p class="mx-2 text-lg mt-8">{{ $t('Chef Recommended Replacements') }}</p>
        <p class="mx-2 text-lg mt-8">{{ $t('Reason') }}:</p>
        <p class="mx-2 text-lg mt-8">{{ order.replacementReason || $t('No reason provided') }}</p>
        <p class="mt-8 mx-6 text-lg">{{ $t('Would you like to consider this instead of your original order') }}?</p>

        <div v-for="(replacement, index) in replacementsParsed" :key="index">
          <div class="border-b border-t border-red-700 flex justify-between px-4 py-6 text-lg mt-8">
            <p>{{ replacement.original.name }}</p>
            <p>${{ replacement.original.price }}</p>
          </div>
          <eva-icon
            class="mt-4 mb-4"
            name="flip-outline"
            animation="pulse"
            width="60"
            height="60"
            fill="#4A5568"
          ></eva-icon>
          <div class="border-b border-t border-green-400 flex justify-between px-4 py-6 text-lg">
            <p>{{ replacement.replacement.name }}</p>
            <p>${{ replacement.replacement.price }}</p>
          </div>
        </div>

        <div class="mt-16 mb-4">
          <button
            class="rounded-full bg-red-700 text-center px-12 py-2 font-light text-white mr-2"
            type="button"
            @click="handleRejectReplacement"
          >
            {{ $t('Reject') }}
          </button>
          <button
            class="rounded-full bg-green-400 text-center px-12 py-2 font-light text-white ml-2"
            type="button"
            @click="handleAcceptReplacement"
          >
            {{ $t('Accept') }}
          </button>
        </div>
      </div>
    </section>
  </div>
</template>

<script>
import moment from 'moment';
import { db } from '@/firebase';

export default {
  name: 'Order',
  data() {
    return {
      order: {},
      countdownMinutes: 0,
      countdownSeconds: 0,
      customerRejectReason: '',
    };
  },
  async created() {
    let order = this.$store.state.orders[this.$route.params.orderId];
    if (order) {
      localStorage.setItem('latestOrder', JSON.stringify(order));
    } else {
      order = JSON.parse(localStorage.getItem('latestOrder'));
    }
    const { restaurant, id } = order;

    await db
      .collection(`/restaurants/${restaurant}/orders`)
      .doc(id)
      .onSnapshot(doc => {
        const updatedOrder = doc.data();
        const { approvedTime, prepTime } = updatedOrder;
        let estimatedTime = '';

        if (approvedTime && prepTime) {
          estimatedTime = moment
            .unix(approvedTime.seconds)
            .add(prepTime, 'minutes')
            .format('h:mm a');
        }

        this.order = { ...updatedOrder, estimatedTime, id: doc.id, restaurant };
        this.countDownTimer();
      });
  },
  methods: {
    handleRejectReplacement() {
      this.$modal.show('rejectReplacementModal');
    },
    async handleConfirmRejectReplacement() {
      const { id, restaurant } = this.order;
      const { customerRejectReason } = this;
      try {
        await db
          .collection(`/restaurants/${restaurant}/orders`)
          .doc(id)
          .update({ status: 'customerRejected', customerRejectReason });
        this.$modal.hide('rejectReplacementModal');
      } catch (e) {
        console.log('e :', e);
      }
    },
    async handleAcceptReplacement() {
      const { items, replacements } = this.order;
      const { id, restaurant } = this.order;

      replacements.forEach(rep => {
        const {
          original: { index, ...rest },
          replacement: { repIndex, ...repRest },
        } = rep;

        items[index] = { ...rest, ...repRest };
      });
      try {
        await db
          .collection(`/restaurants/${restaurant}/orders`)
          .doc(id)
          .update({ status: 'pending', items });
      } catch (e) {
        console.log('e :', e);
      }
    },
    countDownTimer() {
      if (this.order.estimatedTime) {
        const cDown = setInterval(() => {
          const { approvedTime, prepTime } = this.order;

          const eT = moment.unix(approvedTime.seconds).add(prepTime, 'minutes');
          const current = moment();
          const durat = moment.duration(eT.diff(current));
          const minutes = durat.minutes();
          const seconds = durat.seconds();

          if (minutes <= 0 && seconds <= 0) {
            clearInterval(cDown);
          }
          this.countdownMinutes = minutes;
          this.countdownSeconds = seconds;
        }, 1000);
      }
    },
    handleGoToMenu() {
      const { items } = this.order;
      const { restaurant } = items[0];
      if (restaurant) this.$router.push(`/restaurants/${restaurant}`);
    },
    handleGoMyOrders() {
      this.$router.push('/myorders');
    },
  },
  computed: {
    replacementsParsed() {
      if (this.order && this.order.replacements) {
        return this.order.replacements;
      }
      return [];
    },
    itemsFiltered() {
      const d = {};
      if (!this.order.items) return [];
      this.order.items.forEach(i => {
        const { name, price } = i;
        if (d[name]) {
          d[name].quantity += 1;
        } else {
          d[name] = { quantity: 1, price };
        }
      });

      return Object.entries(d);
    },
    cartTotal() {
      if (!this.order.items) return 0;
      return this.order.items.reduce((accumulator, currentValue) => accumulator + currentValue.price, 0);
    },
  },
};
</script>
<style lang="scss">
.place-order-button {
  bottom: 4rem;
}
.replacement-pane {
  height: 90%;
  width: 100%;
}
</style>
